import type { Plan } from '@otter-ai/recall-client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { plansQueryCreator } from '@/query/plansQueryCreator';
import { OtterPlanType, PlanPaymentPeriod } from '@/shared/types/plan';
import { isProEduPlan, isValidPurchasePlan } from '@/shared/utils/planUtils';

export const usePlanOptions = ({
  planType,
  isEdu,
}: {
  planType: OtterPlanType;
  isEdu: boolean;
}) => {
  const { data, isLoading } = useQuery(plansQueryCreator());

  const activePlans = useMemo(() => {
    return data?.filter((plan: Plan) => plan.stripe_plan_id !== null);
  }, [data]);

  const { annualPlan, monthlyPlan } = useMemo(() => {
    const planOptions = activePlans?.filter((plan: Plan) => {
      if (isEdu && planType === OtterPlanType.PREMIUM) {
        return isProEduPlan(plan);
      }
      return isValidPurchasePlan(plan, planType);
    });

    return {
      annualPlan: planOptions?.find(
        (plan: Plan) => plan.payment_period_type === PlanPaymentPeriod.YEAR,
      ),
      monthlyPlan: planOptions?.find(
        (plan: Plan) => plan.payment_period_type === PlanPaymentPeriod.MONTH,
      ),
    };
  }, [activePlans, isEdu, planType]);

  return {
    annualPlan,
    monthlyPlan,
    isLoading,
  };
};
